

import { FC, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import ComponentError from '@eneco/components/ui/ComponentError/ComponentError';
import { postEnecoBeXapiSiteApiV1EligibilitiesAgreementsSign } from '@eneco/dc-be/client';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRouter } from '@eneco/next';
import { Form, Skeleton , Box, Button, Card, Checkbox, Grid, Heading, PageGrid, Stack } from '@eneco/sparky';
import { AgreementRendering } from '@sitecore/types/Agreement';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

interface FormFields {
  isAgreementChecked: boolean;
}

const HAS_SEEN_PRIVACY_COOKIE = 'HAS_SEEN_PRIVACY';

const Agreement: FC<AgreementRendering> = ({
  fields: { title, buttonLink, checkboxLabel, checkboxValidationNotification, content },
}) => {
  const [isLoading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null | undefined>(null);
  const { query, replace } = useRouter();

  const formSchema = yup.object({
    isAgreementChecked: yup.boolean().oneOf([true], checkboxValidationNotification?.value.title),
  });

  const formMethods = useForm<FormFields>({
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods;

  async function executeApiCall(): Promise<ReturnType<typeof postEnecoBeXapiSiteApiV1EligibilitiesAgreementsSign>> {
    const request = { body: { privacyPolicy: true, termsOfService: true } };
    return await postEnecoBeXapiSiteApiV1EligibilitiesAgreementsSign(request);
  }

  const onSubmit = async () => {
    try {
      setLoading(true);
      const result = await executeApiCall();
      if (result.response.ok) {
        document.cookie = `${HAS_SEEN_PRIVACY_COOKIE}=true;path=/`;
        const returnPath = query.returnPath as string | undefined;
        const decodedReturnPath = decodeURIComponent(returnPath || '');
        if (decodedReturnPath && !decodedReturnPath.startsWith('http')) {
          replace(decodedReturnPath);
        } else {
          replace(buttonLink.value.href);
        }
      } else {
        return <ComponentError />;
      }
    } catch {
      setApiError('');
    }
  };

  return (
    <Box paddingY="10" backgroundColor="backgroundSecondary">
      <PageGrid>
        <Grid.Item gridColumn={{ initial: '1/-1', md: '4/-2', lg: '5/9' }} gridRowStart="1">
          {isLoading && <Skeleton height={400} />}
          {!isLoading && !apiError && (
            <Box>
              <Card as="div" corners="rounded" elevation="S">
                <Card.Content>
                  <FormProvider {...formMethods}>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                      <Stack direction="column" gap="6">
                        <Heading size="S" as="h2">
                          {title.value}
                        </Heading>
                        <RichText html={content.value} />
                        <Checkbox
                          label={checkboxLabel.value}
                          {...register('isAgreementChecked')}
                          error={errors?.isAgreementChecked?.message}
                        />
                        <Button action="primary" size="regular" tone="onLight" type="submit" isLoading={isLoading}>
                          {buttonLink.value.title}
                        </Button>
                      </Stack>
                    </Form>
                  </FormProvider>
                </Card.Content>
              </Card>
            </Box>
          )}
          {apiError && <ComponentError />}
        </Grid.Item>
      </PageGrid>
    </Box>
  );
};

export default Agreement;
